import { UnauthorizedError } from '../../errors/UnauthorizedError';

import type { CasinoConfigType, ICasinoConfigJson } from './types';
import {
  areRequiredFilled,
  booleanFormatter,
  parseFormatter,
  splitFormatter,
  tablesFormatter,
  tabsFormatter,
} from './utils';

export const xml2CasinoConfig = (str: string): CasinoConfigType => {
  const xml = new window.DOMParser().parseFromString(str, 'text/xml');
  const elementsByTagName = xml.getElementsByTagName('casinoconfig-param');
  const sessionStatus = xml.getElementsByTagName('session');
  const isSessionExprired = sessionStatus[0]?.textContent === 'offline';

  if (isSessionExprired) {
    throw new UnauthorizedError('xml2CasinoConfig');
  }
  const result: Partial<CasinoConfigType> = {};
  for (const element of elementsByTagName) {
    for (const attr of (element as Element).attributes) {
      const item = mapXmlAttrToTypeField(attr, element);
      const field = item.field as keyof CasinoConfigType;
      if (field) {
        result[field] = item.value as never; //TODO: Really not the best way to handle that
      }
    }
  }

  // Checks if required fields are filled, only required fields for now are casinoId and casinoName
  if (!areRequiredFilled(result)) {
    throw new Error('Casino config is not full');
  }
  return result as CasinoConfigType;
};

const mapXmlAttrToTypeField = (
  attr: Attr,
  element: Element
): { field?: string; value?: string | boolean | Record<string, string[]> | string[] } => {
  switch (attr.name) {
    case 'bonus_balance':
      return { field: 'showBonusBalance', value: attr.value === 'true' };
    case 'cashier_iframe_event':
      return { field: 'cashierIframeEvent', value: attr?.value === 'true' };
    case 'cashier_mobile':
      return { field: 'cashierUrlMobile', value: attr.value };
    case 'cashier_popup_enabled':
      return { field: 'cashierPopupEnabled', value: attr.value === 'true' };
    case 'cashier_url':
      return { field: 'cashierUrl', value: attr.value };
    case 'cashier_url_first':
      return { field: 'cashierUrlFirst', value: attr.value };
    case 'casinoRegion':
      return { field: 'casinoRegion', value: attr.value };
    case 'casino_id':
      return { field: 'casinoId', value: attr.value };
    case 'casino_name':
      return { field: 'casinoName', value: attr.value };
    case 'chat_disabled':
      return { field: 'chatDisabled', value: attr.value === 'true' };
    case 'exit_url':
      return { field: 'exitUrl', value: attr.value };
    case 'hide_bonus':
      return { field: 'hideBonusBalance', value: attr.value === 'true' };
    case 'hide_currency':
      return { field: 'hideCurrency', value: attr.value === 'true' };
    case 'hide_lobby_buttons':
      return { field: 'hideLobbyButtons', value: attr.value === 'true' };
    case 'hidefullscreenbtn':
      return { field: 'hideFullScreenBtn', value: attr.value === 'true' };
    case 'home_url':
      return { field: 'homeUrl', value: attr.value };
    case 'integration_api':
      return { field: 'integrationApi', value: attr.value.split(',') };
    case 'ismanualpayout':
      return { field: 'isManualPayout', value: attr.value == 'true' };
    case 'italianlobbyenabled':
      return { field: 'isSessionBalanceEnabled', value: attr.value === 'true' };
    case 'lobby_version':
      return { field: 'lobbyVersion', value: attr.value };
    case 'languages':
      return { field: 'languages', value: splitFormatter(attr.value) };
    case 'net_position':
      return { field: 'forceHideNetPosition', value: attr.value === 'false' };
    case 'notify_close_container':
      return { field: 'notifyCloseContainer', value: attr.value === 'true' };
    case 'remove_autoplay_uk':
      return { field: 'hideAutoplay', value: attr.value === 'true' };
    case 'return_main_page':
      return { field: 'returnMainPage', value: attr.value === 'true' };
    case 'show_alderney_msg':
      return { field: 'showAlderneyMsg', value: attr.value === 'true' };
    case 'show_nickname_disclaimer':
      return { field: 'showNicknameDisclaimer', value: attr.value === 'true' };
    case 'slotslobbyenabled':
      return { field: 'slotsLobbyEnabled', value: attr.value === 'true' };
    case 'type':
      switch (attr.value) {
        case 'tabs_order':
          return { field: 'tabs', value: JSON.parse(element.innerHTML).tabs };
        case 'tables_order': {
          const tables: Record<string, string[]> = {};
          const parsedTables = JSON.parse(element.innerHTML);
          for (const table in parsedTables) {
            tables[table.toUpperCase()] = parsedTables[table];
          }
          return { field: 'tables', value: tables };
        }
        default:
          return { field: undefined, value: undefined };
      }
    case 'voice_over_tables':
      return { field: 'voiceOverTables', value: attr.value.split(',') };
    default:
      return { field: undefined, value: undefined };
  }
};

export const casinoConfigParserJson = (response: ICasinoConfigJson): CasinoConfigType => {
  const params = response.params;
  return {
    cashierIframeEvent: booleanFormatter(params.cashier_iframe_event),
    cashierPopupEnabled: booleanFormatter(params.cashier_popup_enabled),
    cashierUrl: params.cashier_url,
    cashierUrlFirst: params.cashier_url_first,
    cashierUrlMobile: params.cashier_mobile,
    casinoId: response.casinoId,
    casinoName: response.casinoName,
    casinoRegion: response.casinoRegion,
    chatDisabled: booleanFormatter(params.chat_disabled),
    exitUrl: params.exit_url,
    forceHideNetPosition: params.net_position === 'false',
    heroBanners: splitFormatter(params.hero_banners),
    hideAutoplay: booleanFormatter(params.remove_autoplay_uk),
    hideBonusBalance: booleanFormatter(params.hide_bonus),
    hideCurrency: booleanFormatter(params.hide_currency),
    hideFullScreenBtn: booleanFormatter(params.hidefullscreenbtn),
    hideLobbyButtons: booleanFormatter(params.hide_lobby_buttons),
    homeUrl: response.params.home_url,
    integrationApi: splitFormatter(params.integration_api),
    isManualPayout: params.ismanualpayout == 'true', // FIXME: not strict check?
    isMicroGamingEnabled: booleanFormatter(params.micro_gaming),
    isSessionBalanceEnabled: booleanFormatter(params.italianlobbyenabled),
    lobbyVersion: params.lobby_version,
    languages: splitFormatter(params.languages),
    notifyCloseContainer: booleanFormatter(params.notify_close_container),
    returnMainPage: booleanFormatter(params.return_main_page),
    showAlderneyMsg: booleanFormatter(params.show_alderney_msg),
    showBonusBalance: booleanFormatter(params.bonus_balance),
    showNicknameDisclaimer: booleanFormatter(params.show_nickname_disclaimer),
    slotsLobbyEnabled: booleanFormatter(params.slotslobbyenabled),
    tables: tablesFormatter(parseFormatter(params.tables_order)),
    tabs: tabsFormatter(parseFormatter(params.tabs_order)),
    voiceOverTables: splitFormatter(params.voice_over_tables),
  };
};
